import './Search.scss'

import { useContext, useState } from 'react'
import { Button, Row, Form, InputGroup, Spinner, Col } from 'react-bootstrap'
import SearchResult from '../SearchResult/SearchResult'
import { getSearchResults } from 'components/common/api/Search'
import { LMSKeys } from 'components/common/api/constants'
import {
  EVENT_ACTION_SEARCH,
  EVENT_CATEGORY_SEARCH,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { LMS_TYPE_TOPIC } from 'components/common/api/Courses'
import CourseContext from 'components/common/contexts/CourseContext'

/**
 * Search component
 * @param {Object} params The component parameters
 * @param {Function} params.hideHandler The function to call when the search should be hidden
 * @returns
 */
export default function Search({ hideHandler = null, returnPath = null }) {
  const { courses } = useContext(CourseContext)

  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState()
  const [resultCount, setResultCount] = useState(0)
  const [searchTerms, setSearchTerms] = useState('')
  const [staticTerm, setStaticTerm] = useState()

  const getResults = async event => {
    event.preventDefault()

    const trimmedTerms = searchTerms.trim()

    // If the search terms are empty, do nothing
    if (!trimmedTerms) return

    setIsLoading(true)

    // Log the search
    saveUserEvent(EVENT_CATEGORY_SEARCH, EVENT_ACTION_SEARCH, trimmedTerms)

    const results = await getSearchResults(trimmedTerms)

    // Did we get results?
    if (results) {
      // Create a list of allowed keys for the search results
      const resultKeys = LMSKeys

      const filteredResults = results.filter(
        result => resultKeys[result.subtype]?.showInSearch
      )

      // Do we have any Topics? If so, let's store them in a separate array
      const topics = results.filter(result => result.subtype === LMS_TYPE_TOPIC)

      // Get the number of results
      const count = filteredResults.length + topics.length

      // Now that we have the filtered results, let's sort them by subtype. Start by creating a
      // new object to hold the sorted results
      let groupedResults = {}

      // Loop through the filtered results and add them to the sorted results object
      filteredResults.forEach(result => {
        // If we don't have a key for this subtype, create it
        if (!groupedResults[result.subtype]) groupedResults[result.subtype] = []

        // Add the result to the sorted results object
        groupedResults[result.subtype].push(result)
      })

      // If we have any Topics, add them to the sorted results object by finding the parent Course and adding them to it
      if (topics.length) {
        let courseGroup = {}

        // Loop through the Topics
        topics.forEach(topic => {
          const courseId = topic.course_meta?.course_id

          // If we don't have a key for this Course, create it
          if (!courseGroup[courseId]) courseGroup[courseId] = []

          // Add the result to courseGroup
          courseGroup[courseId].push(topic)
        })

        // Now that we have the Topics grouped by Course, let's add them to the sorted results object
        Object.keys(courseGroup).forEach(courseId => {
          // Find the parent Course
          let course = courses?.find(course => course.id === parseInt(courseId))

          // If we don't have a parent Course, skip this Topic
          if (!course) return

          const matchedTopics = courseGroup[courseId]

          // Add the topics to the Course
          course.topics = matchedTopics

          // If we don't have a key for this subtype, create it
          if (!groupedResults[course.type]) {
            groupedResults[course.type] = []

            // Add the Course to the sorted results object
            groupedResults[course.type].push(course)
          } else {
            // Look to see if the Course is already in the sorted results object
            const existingCourseIndex = groupedResults[course.type].findIndex(
              result => result.id === course.id
            )

            // If the Course is already in the sorted results object, append the Topics to it
            if (existingCourseIndex > -1) {
              groupedResults[course.type][existingCourseIndex].topics =
                matchedTopics
            } else {
              // Add the Course to the sorted results object
              groupedResults[course.type].push(course)
            }
          }
        })
      }

      // Now that the results are grouped by type, let's create an array of results to display,
      // ordered by the `order` property of the `resultKeys` object
      const sortedResults = Object.keys(groupedResults)
        .sort((a, b) => resultKeys[a].order - resultKeys[b].order)
        .map(key => {
          return {
            ...resultKeys[key],
            results: groupedResults[key]
          }
        })

      // Update the results
      setSearchResults(sortedResults)
      setResultCount(count)
    }

    setIsLoading(false)
    setStaticTerm(trimmedTerms)
  }

  return (
    <div className='search-wrapper'>
      <Row className='p-0'>
        <Col className='p-0'>
          <Form className='search' onSubmit={e => getResults(e)}>
            <InputGroup>
              <Form.Control
                type='text'
                placeholder='Search'
                disabled={isLoading}
                value={searchTerms}
                onChange={e => setSearchTerms(e.target.value)}
                autoFocus
              />
              <Button
                variant='outline-primary'
                id='search-button'
                title='Search'
                disabled={isLoading}
                type='submit'
              >
                <i className='icon bi-search'></i>
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col>
          {isLoading ? (
            <div className='text-center'>
              <Spinner
                className='m-5'
                animation='border'
                role='status'
                variant='primary'
                size='lg'
              >
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
              <p>Searching...</p>
            </div>
          ) : (
            staticTerm && (
              <h4 className='mt-3 muted px-3'>
                {resultCount} result
                {resultCount !== 1 ? 's' : ''} for <i>{staticTerm}</i>
              </h4>
            )
          )}
        </Col>
      </Row>
      {!isLoading &&
        searchResults &&
        searchResults.map((type, typeIndex) => (
          <Row key={typeIndex}>
            <Col>
              <h3 className='result-group-title px-3'>
                {type?.title}{' '}
                <small className='muted'>({type?.results?.length})</small>
              </h3>
              {type?.results?.map((result, index) => (
                <SearchResult
                  result={result}
                  index={index}
                  key={index}
                  hideHandler={hideHandler}
                  term={staticTerm}
                  returnPath={returnPath}
                />
              ))}
            </Col>
          </Row>
        ))}
    </div>
  )
}
